import { Card } from '../components/Card';

export function Privacy() {
  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <h1 className="font-display text-3xl uppercase tracking-wide">Privacy</h1>
      <div className="font-mono text-[10px] text-text-faint">Effective 19 May 2026</div>

      <Card>
        <p className="text-sm text-text-base leading-relaxed">
          PunditXI is a football prediction game distributed as a Telegram Mini App. This Privacy
          Notice explains what personal data we collect about you, how we use it, and your rights
          under the UK GDPR and Data Protection Act 2018. It is written in plain English on
          purpose.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">Who we are</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          PunditXI is operated by James Cox, a sole trader based in England. We are the data
          controller for the personal data described below. Contact us at{' '}
          <a className="text-trophy-gold" href="mailto:privacy@punditxi.app">
            privacy@punditxi.app
          </a>{' '}
          for any privacy matter.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">What we collect</h2>
        <ul className="space-y-2 text-sm text-text-dim list-disc list-inside">
          <li>
            <strong className="text-text-base">Telegram identity</strong> — your Telegram user ID,
            first name, last name (if set), username (if set), language code, profile photo URL.
            We receive these in cryptographically-signed form from Telegram when you open the Mini
            App and validate the signature before trusting them.
          </li>
          <li>
            <strong className="text-text-base">Gameplay data</strong> — your match predictions,
            bracket picks, points awarded, streaks, league memberships, and notification
            preferences.
          </li>
          <li>
            <strong className="text-text-base">Payment records</strong> — Telegram Stars charge
            IDs, amounts, product codes, and timestamps. We do not see or store payment-card
            details; all payments are processed by Telegram in Stars (XTR).
          </li>
          <li>
            <strong className="text-text-base">Usage analytics</strong> — page views, button taps,
            and product events (e.g. "prediction_submitted"), processed by PostHog with your
            Telegram user ID as the identifier.
          </li>
          <li>
            <strong className="text-text-base">Error reports</strong> — stack traces and request
            metadata sent to Sentry when something breaks. We do not include the body of your
            predictions or any sensitive content.
          </li>
        </ul>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">What we don't collect</h2>
        <ul className="space-y-2 text-sm text-text-dim list-disc list-inside">
          <li>Your email address, phone number, or postal address</li>
          <li>Your payment-card or bank-account details</li>
          <li>Your Telegram contacts, messages, photos, or location</li>
          <li>Any third-party tracking identifiers (we do not use ad networks)</li>
        </ul>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">Lawful basis</h2>
        <ul className="space-y-2 text-sm text-text-dim list-disc list-inside">
          <li>
            <strong className="text-text-base">Contract</strong> — we process the gameplay and
            payment data above to provide the service you've signed up for.
          </li>
          <li>
            <strong className="text-text-base">Legitimate interests</strong> — we process analytics
            and error data to improve the service and prevent abuse. You can opt out of bot
            notifications at any time with{' '}
            <code className="font-mono text-trophy-gold">/notifications off</code>.
          </li>
          <li>
            <strong className="text-text-base">Consent</strong> — for any optional processing we
            add in future (e.g. marketing), we'll ask first.
          </li>
        </ul>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">Who we share it with</h2>
        <p className="text-sm text-text-dim leading-relaxed mb-2">
          We use these data processors. Each has signed contractual safeguards (typically standard
          contractual clauses where data crosses borders) and accesses only what they need to do
          their job:
        </p>
        <ul className="space-y-2 text-sm text-text-dim list-disc list-inside">
          <li>
            <strong className="text-text-base">Cloudflare</strong> (USA) — hosting, edge compute,
            database (D1).
          </li>
          <li>
            <strong className="text-text-base">PostHog EU</strong> (Germany) — product analytics.
          </li>
          <li>
            <strong className="text-text-base">Sentry</strong> (Germany / USA) — error monitoring.
          </li>
          <li>
            <strong className="text-text-base">Telegram</strong> (UK / Dubai) — identity, payments,
            and the messaging surface itself.
          </li>
          <li>
            <strong className="text-text-base">API-Football</strong> (France) — fixture and result
            data; we send no personal data to them.
          </li>
        </ul>
        <p className="text-sm text-text-dim leading-relaxed mt-2">
          We do not sell your data to anyone. We do not share it with advertisers.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">International transfers</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          Some of the processors above are based outside the UK. Where data is transferred outside
          the UK or EEA, we rely on the UK International Data Transfer Agreement, EU Standard
          Contractual Clauses, or adequacy decisions, as appropriate.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">How long we keep it</h2>
        <ul className="space-y-2 text-sm text-text-dim list-disc list-inside">
          <li>
            Account, gameplay, and payment data — while your account is active and for 30 days
            after deletion (so we can recover an accidentally-deleted account or resolve disputes).
          </li>
          <li>Anonymous, aggregated leaderboard statistics may be retained indefinitely.</li>
          <li>Analytics events — 12 months by default in PostHog.</li>
          <li>Error reports — 90 days in Sentry.</li>
        </ul>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">Your rights</h2>
        <p className="text-sm text-text-dim leading-relaxed mb-2">
          Under UK GDPR you have the right to:
        </p>
        <ul className="space-y-2 text-sm text-text-dim list-disc list-inside">
          <li>
            <strong className="text-text-base">Access</strong> — request a copy of the personal
            data we hold about you (we'll send it in JSON).
          </li>
          <li>
            <strong className="text-text-base">Rectification</strong> — ask us to correct
            inaccurate data.
          </li>
          <li>
            <strong className="text-text-base">Erasure</strong> — ask us to delete your account
            and personal data.
          </li>
          <li>
            <strong className="text-text-base">Portability</strong> — receive your data in a
            structured machine-readable format.
          </li>
          <li>
            <strong className="text-text-base">Restriction & objection</strong> — ask us to pause
            certain processing.
          </li>
        </ul>
        <p className="text-sm text-text-dim leading-relaxed mt-2">
          Email{' '}
          <a className="text-trophy-gold" href="mailto:privacy@punditxi.app">
            privacy@punditxi.app
          </a>{' '}
          to exercise any of these — we'll respond within 30 days. You can also complain to the
          UK Information Commissioner's Office (ICO) at{' '}
          <a className="text-trophy-gold" href="https://ico.org.uk" target="_blank" rel="noreferrer">
            ico.org.uk
          </a>
          .
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">Storage in the app</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          The Mini App stores a JWT session token in Telegram CloudStorage (or browser localStorage
          if you open the app outside Telegram). It's used to authenticate your requests to our
          servers. We do not use third-party advertising cookies.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">Children</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          PunditXI is for users 18 and over. Telegram Stars purchases on iOS and Android are
          age-gated by the app stores. If we learn that an account belongs to a person under 18,
          we will delete it.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">Changes</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          If we make material changes to this notice, we'll update the "Effective" date at the top
          and notify you in the Mini App or via a one-off bot message. You can always read the
          latest version at{' '}
          <a className="text-trophy-gold" href="https://punditxi.app/privacy">
            punditxi.app/privacy
          </a>
          .
        </p>
      </Card>
    </div>
  );
}
